/**
 * Count-up — numbers marked `data-count-to="38"` (or "2,5") run up from zero the first time
 * they scroll into view.
 *
 * The real value is already in the HTML, so nothing is lost without JS, and the element's
 * width is reserved server-side (min-width in ch, tabular figures) so the counting never
 * shifts the text beside it. Runs in the critical pass: the trust bar sits right under the
 * hero, and resetting a number that is already on screen would read as a flicker.
 */
export function countUp() {
  const nodes = document.querySelectorAll('[data-count-to]');
  if (!nodes.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const parse = (raw) => {
    const [whole, frac = ''] = String(raw).split(',');
    return { target: parseFloat(`${whole}.${frac || 0}`), decimals: frac.length };
  };
  const format = (n, decimals) => n.toFixed(decimals).replace('.', ',');
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  const run = (el, delay) => {
    const { target, decimals } = parse(el.dataset.countTo);
    if (!Number.isFinite(target)) return;
    const duration = 1300 + Math.min(target, 60) * 6;
    setTimeout(() => {
      const start = performance.now();
      const tick = (now) => {
        // rAF's timestamp is the frame's start, which can precede `start` — clamp at both ends.
        const t = Math.max(0, Math.min(1, (now - start) / duration));
        el.textContent = format(target * ease(t), decimals);
        if (t < 1) requestAnimationFrame(tick);
        else el.textContent = el.dataset.countTo;
      };
      requestAnimationFrame(tick);
    }, delay);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        // Siblings in the same list start one after another, so the row reads left to right.
        const list = entry.target.closest('ul, ol');
        const index = list ? [...list.querySelectorAll('[data-count-to]')].indexOf(entry.target) : 0;
        run(entry.target, Math.max(0, index) * 140);
      });
    },
    { threshold: 0.6 },
  );

  nodes.forEach((el) => {
    const { decimals } = parse(el.dataset.countTo);
    el.textContent = format(0, decimals);
    observer.observe(el);
  });
}
